/**
 * Canvas drawing for the plan.
 *
 * Pure rendering: it reads the level and the viewport and paints, holding no
 * state of its own. Openings are drawn by painting the wall's background back
 * over it, which is both how a floor plan reads and cheaper than building
 * per-wall geometry with holes in it.
 */

import { distance } from '../model/geometry';
import { openingEnds, wallEnds } from '../model/graph';
import type { Room } from '../model/rooms';
import type { Level, Opening, Point } from '../model/types';
import { toScreen, type Viewport } from './viewport';

export type Selection =
  | { kind: 'wall'; id: string }
  | { kind: 'junction'; id: string }
  | { kind: 'opening'; id: string }
  | { kind: 'room'; id: string };

export interface RenderOptions {
  level: Level;
  rooms: Room[];
  viewport: Viewport;
  /** Canvas size in CSS pixels; the context is already scaled for the DPR. */
  width: number;
  height: number;
  backdrop: HTMLImageElement | null;
  showBackdrop: boolean;
  selection: Selection | null;
  hoverWallId: string | null;
  /** The wall being drawn right now, if any. */
  preview: { from: Point; to: Point } | null;
  /** Where the next click would land, after snapping. */
  snapPoint: Point | null;
  /** Points picked so far by the calibrate tool. */
  calibrationPoints: Point[];
}

const palette = {
  background: '#f7f6f3',
  grid: '#e6e3dd',
  gridStrong: '#d8d4cb',
  wall: '#2b2b2b',
  wallSelected: '#c2410c',
  wallHover: '#6b7280',
  loadBearing: '#4b5563',
  room: 'rgba(96, 165, 250, 0.13)',
  roomSelected: 'rgba(249, 115, 22, 0.18)',
  roomStroke: 'rgba(59, 130, 246, 0.35)',
  junction: '#374151',
  label: '#1f2937',
  labelMuted: '#6b7280',
  preview: '#2563eb',
  snap: '#059669',
  calibration: '#7c3aed',
  warning: '#b91c1c',
};

export function render(ctx: CanvasRenderingContext2D, options: RenderOptions): void {
  const { width, height } = options;

  ctx.save();
  ctx.fillStyle = palette.background;
  ctx.fillRect(0, 0, width, height);

  if (options.showBackdrop && options.backdrop) drawBackdrop(ctx, options);
  drawGrid(ctx, options.viewport, width, height);
  drawRooms(ctx, options);
  drawWalls(ctx, options);
  drawOpenings(ctx, options);
  drawJunctions(ctx, options);
  drawRoomLabels(ctx, options);
  drawPreview(ctx, options);
  drawCalibration(ctx, options);
  drawSnap(ctx, options);
  drawScaleBar(ctx, options);
  ctx.restore();
}

/**
 * A bar showing what a round real distance measures on screen.
 *
 * Calibrating rescales the geometry and the backdrop together, so the picture
 * afterwards can look identical -- correctly so, but it leaves the user with no
 * sign that anything happened. This is the thing that visibly changes, and the
 * conventional way to read a plan's scale besides.
 */
function drawScaleBar(ctx: CanvasRenderingContext2D, options: RenderOptions): void {
  const { width, height, viewport } = options;

  // The longest round distance that still fits in a sensible bar.
  const steps = [100, 250, 500, 1000, 2000, 5000, 10000, 20000, 50000];
  const millimetres =
    [...steps].reverse().find((mm) => mm * viewport.zoom <= 160) ?? steps[0];
  const barPixels = millimetres * viewport.zoom;
  if (barPixels < 20) return;

  const right = width - 16;
  const left = right - barPixels;
  const y = height - 18;
  const label = millimetres >= 1000 ? `${millimetres / 1000} m` : `${millimetres} mm`;

  ctx.save();
  ctx.font = '11px system-ui, sans-serif';
  const labelWidth = ctx.measureText(label).width;

  ctx.fillStyle = 'rgba(255, 255, 255, 0.88)';
  ctx.fillRect(left - 8, y - 14, barPixels + labelWidth + 24, 26);

  ctx.strokeStyle = palette.label;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(left, y - 4);
  ctx.lineTo(left, y + 4);
  ctx.moveTo(left, y);
  ctx.lineTo(right, y);
  ctx.moveTo(right, y - 4);
  ctx.lineTo(right, y + 4);
  ctx.stroke();

  ctx.fillStyle = palette.label;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, left - 10, y);
  ctx.restore();
}

function drawBackdrop(ctx: CanvasRenderingContext2D, options: RenderOptions): void {
  const backdrop = options.level.backdrop;
  const image = options.backdrop;
  if (!backdrop || !image) return;

  const { transform } = backdrop;
  const origin = toScreen(options.viewport, { x: transform.x, y: transform.y });
  // One image pixel covers mmPerPixel millimetres, which the viewport then
  // scales to canvas pixels.
  const scale = transform.mmPerPixel * options.viewport.zoom;

  ctx.save();
  ctx.globalAlpha = backdrop.opacity;
  ctx.translate(origin.x, origin.y);
  ctx.rotate(transform.rotation);
  ctx.scale(scale, scale);
  ctx.drawImage(image, 0, 0);
  ctx.restore();
}

function drawGrid(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  width: number,
  height: number,
): void {
  // Step up through 0.1 / 1 / 10 m as the view zooms out, so the grid never
  // becomes a solid block of lines.
  const candidates = [100, 500, 1000, 5000, 10000];
  const step = candidates.find((mm) => mm * viewport.zoom >= 8) ?? candidates[candidates.length - 1];

  const left = viewport.originX;
  const top = viewport.originY;
  const right = left + width / viewport.zoom;
  const bottom = top + height / viewport.zoom;

  ctx.save();
  ctx.lineWidth = 1;
  for (let x = Math.ceil(left / step) * step; x < right; x += step) {
    const screenX = Math.round((x - left) * viewport.zoom) + 0.5;
    ctx.strokeStyle = x % (step * 10) === 0 ? palette.gridStrong : palette.grid;
    ctx.beginPath();
    ctx.moveTo(screenX, 0);
    ctx.lineTo(screenX, height);
    ctx.stroke();
  }
  for (let y = Math.ceil(top / step) * step; y < bottom; y += step) {
    const screenY = Math.round((y - top) * viewport.zoom) + 0.5;
    ctx.strokeStyle = y % (step * 10) === 0 ? palette.gridStrong : palette.grid;
    ctx.beginPath();
    ctx.moveTo(0, screenY);
    ctx.lineTo(width, screenY);
    ctx.stroke();
  }
  ctx.restore();
}

function tracePolygon(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  polygon: Point[],
): void {
  ctx.beginPath();
  polygon.forEach((point, index) => {
    const screen = toScreen(viewport, point);
    if (index === 0) ctx.moveTo(screen.x, screen.y);
    else ctx.lineTo(screen.x, screen.y);
  });
  ctx.closePath();
}

function drawRooms(ctx: CanvasRenderingContext2D, options: RenderOptions): void {
  ctx.save();
  for (const room of options.rooms) {
    const selected = options.selection?.kind === 'room' && options.selection.id === room.id;
    tracePolygon(ctx, options.viewport, room.polygon);
    ctx.fillStyle = selected ? palette.roomSelected : palette.room;
    ctx.fill();
    ctx.strokeStyle = palette.roomStroke;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
  ctx.restore();
}

function drawWalls(ctx: CanvasRenderingContext2D, options: RenderOptions): void {
  ctx.save();
  ctx.lineCap = 'butt';

  for (const wall of options.level.walls) {
    const ends = wallEnds(options.level, wall);
    if (!ends) continue;

    const a = toScreen(options.viewport, ends.a);
    const b = toScreen(options.viewport, ends.b);
    const selected = options.selection?.kind === 'wall' && options.selection.id === wall.id;
    const hovered = options.hoverWallId === wall.id;

    ctx.strokeStyle = selected
      ? palette.wallSelected
      : hovered
        ? palette.wallHover
        : wall.loadBearing
          ? palette.loadBearing
          : palette.wall;
    // Never thinner than a hairline, or a wall vanishes when zoomed out.
    ctx.lineWidth = Math.max(1.5, wall.thickness * options.viewport.zoom);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();

    // Load-bearing walls get a hatch down the centre so they read differently
    // from partitions without relying on colour alone.
    if (wall.loadBearing && wall.thickness * options.viewport.zoom > 4) {
      ctx.save();
      ctx.strokeStyle = palette.background;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      ctx.restore();
    }
  }
  ctx.restore();
}

/** Fills the corners that butt-capped wall strokes leave open. */
function drawJunctions(ctx: CanvasRenderingContext2D, options: RenderOptions): void {
  const thicknessAt = new Map<string, number>();
  for (const wall of options.level.walls) {
    thicknessAt.set(wall.a, Math.max(thicknessAt.get(wall.a) ?? 0, wall.thickness));
    thicknessAt.set(wall.b, Math.max(thicknessAt.get(wall.b) ?? 0, wall.thickness));
  }

  ctx.save();
  for (const junction of options.level.junctions) {
    const screen = toScreen(options.viewport, junction);
    const thickness = thicknessAt.get(junction.id) ?? 0;
    const radius = Math.max(0.75, (thickness / 2) * options.viewport.zoom);

    ctx.fillStyle = palette.wall;
    ctx.beginPath();
    ctx.arc(screen.x, screen.y, radius, 0, Math.PI * 2);
    ctx.fill();

    const selected = options.selection?.kind === 'junction' && options.selection.id === junction.id;
    if (selected) {
      ctx.strokeStyle = palette.wallSelected;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(screen.x, screen.y, Math.max(radius + 3, 6), 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  ctx.restore();
}

function drawOpenings(ctx: CanvasRenderingContext2D, options: RenderOptions): void {
  ctx.save();
  for (const opening of options.level.openings) {
    const ends = openingEnds(options.level, opening);
    if (!ends) continue;

    const wall = options.level.walls.find((w) => w.id === opening.wall);
    if (!wall) continue;

    const a = toScreen(options.viewport, ends.a);
    const b = toScreen(options.viewport, ends.b);
    const wallWidth = Math.max(1.5, wall.thickness * options.viewport.zoom);

    // Cut the hole by painting the background back over the wall.
    ctx.strokeStyle = palette.background;
    ctx.lineCap = 'butt';
    ctx.lineWidth = wallWidth;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();

    const selected = options.selection?.kind === 'opening' && options.selection.id === opening.id;
    ctx.strokeStyle = selected ? palette.wallSelected : palette.wall;
    ctx.lineWidth = 1.25;

    if (opening.type === 'window') drawWindow(ctx, a, b, wallWidth);
    else if (opening.type === 'door') drawDoor(ctx, a, b, wallWidth, opening);
    else drawJambs(ctx, a, b, wallWidth);
  }
  ctx.restore();
}

/** Two lines across the reveal, the conventional window symbol. */
function drawWindow(ctx: CanvasRenderingContext2D, a: Point, b: Point, wallWidth: number): void {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = (-dy / len) * (wallWidth / 4);
  const ny = (dx / len) * (wallWidth / 4);

  for (const sign of [1, -1]) {
    ctx.beginPath();
    ctx.moveTo(a.x + nx * sign, a.y + ny * sign);
    ctx.lineTo(b.x + nx * sign, b.y + ny * sign);
    ctx.stroke();
  }
}

/** A leaf and its swing arc. */
function drawDoor(
  ctx: CanvasRenderingContext2D,
  a: Point,
  b: Point,
  wallWidth: number,
  opening: Opening,
): void {
  drawJambs(ctx, a, b, wallWidth);

  const width = distance(a, b);
  if (width < 6) return;

  const swing = opening.swing ?? 'left-in';
  const hinge = swing.startsWith('left') ? a : b;
  const other = hinge === a ? b : a;
  const side = swing.endsWith('in') ? 1 : -1;

  const dx = other.x - hinge.x;
  const dy = other.y - hinge.y;
  const start = Math.atan2(dy, dx);
  const end = start + (Math.PI / 2) * side;

  ctx.beginPath();
  ctx.arc(hinge.x, hinge.y, width, Math.min(start, end), Math.max(start, end));
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(hinge.x, hinge.y);
  ctx.lineTo(hinge.x + Math.cos(end) * width, hinge.y + Math.sin(end) * width);
  ctx.stroke();
}

/** Short ticks marking where the wall stops either side of the hole. */
function drawJambs(ctx: CanvasRenderingContext2D, a: Point, b: Point, wallWidth: number): void {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = (-dy / len) * (wallWidth / 2);
  const ny = (dx / len) * (wallWidth / 2);

  for (const point of [a, b]) {
    ctx.beginPath();
    ctx.moveTo(point.x + nx, point.y + ny);
    ctx.lineTo(point.x - nx, point.y - ny);
    ctx.stroke();
  }
}

function drawRoomLabels(ctx: CanvasRenderingContext2D, options: RenderOptions): void {
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  for (const room of options.rooms) {
    const screen = toScreen(options.viewport, room.seed);
    const name = room.meta?.name;
    const declared = room.meta?.declaredAreaM2;
    const area = `${room.areaM2.toFixed(1)} m²`;

    if (name) {
      ctx.fillStyle = palette.label;
      ctx.font = '600 13px system-ui, sans-serif';
      ctx.fillText(name, screen.x, screen.y - 9);
    }

    ctx.fillStyle = palette.labelMuted;
    ctx.font = '12px system-ui, sans-serif';
    ctx.fillText(area, screen.x, screen.y + (name ? 8 : 0));

    // Flag a traced room that disagrees with the printed area by more than 5 %:
    // it usually means the calibration is off, not that the plan is wrong.
    if (declared && Math.abs(room.areaM2 - declared) / declared > 0.05) {
      ctx.fillStyle = palette.warning;
      ctx.font = '11px system-ui, sans-serif';
      ctx.fillText(`plan: ${declared.toFixed(1)} m²`, screen.x, screen.y + (name ? 23 : 15));
    }
  }
  ctx.restore();
}

function drawPreview(ctx: CanvasRenderingContext2D, options: RenderOptions): void {
  if (!options.preview) return;

  const a = toScreen(options.viewport, options.preview.from);
  const b = toScreen(options.viewport, options.preview.to);

  ctx.save();
  ctx.strokeStyle = palette.preview;
  ctx.lineWidth = 2;
  ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
  ctx.setLineDash([]);

  const lengthMm = distance(options.preview.from, options.preview.to);
  drawBadge(ctx, (a.x + b.x) / 2, (a.y + b.y) / 2 - 16, `${(lengthMm / 1000).toFixed(2)} m`);
  ctx.restore();
}

function drawCalibration(ctx: CanvasRenderingContext2D, options: RenderOptions): void {
  if (options.calibrationPoints.length === 0) return;

  ctx.save();
  ctx.strokeStyle = palette.calibration;
  ctx.fillStyle = palette.calibration;
  ctx.lineWidth = 2;

  const screens = options.calibrationPoints.map((point) => toScreen(options.viewport, point));
  for (const screen of screens) {
    ctx.beginPath();
    ctx.arc(screen.x, screen.y, 5, 0, Math.PI * 2);
    ctx.fill();
  }
  if (screens.length === 2) {
    ctx.beginPath();
    ctx.moveTo(screens[0].x, screens[0].y);
    ctx.lineTo(screens[1].x, screens[1].y);
    ctx.stroke();
  }
  ctx.restore();
}

function drawSnap(ctx: CanvasRenderingContext2D, options: RenderOptions): void {
  if (!options.snapPoint) return;

  const screen = toScreen(options.viewport, options.snapPoint);
  ctx.save();
  ctx.strokeStyle = palette.snap;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(screen.x, screen.y, 6, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

function drawBadge(ctx: CanvasRenderingContext2D, x: number, y: number, text: string): void {
  ctx.save();
  ctx.font = '12px system-ui, sans-serif';
  const width = ctx.measureText(text).width + 12;

  ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.strokeStyle = palette.preview;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(x - width / 2, y - 10, width, 20, 4);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = palette.label;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x, y);
  ctx.restore();
}
